import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DashboardWidget } from 'app/viewmodels/dashboard/dashboard-widget';
import { LazyLoadEvent } from 'primeng/api';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { TableConfig } from '../../../../component-library/table/table-config';
import { Widget } from '../../../../viewmodels/dashboard/widget';
import { ItemizedWidgetDataService } from '../itemized-widget-data.service';
import { ItemizedWidgetDataFactory } from '../itemized-widget-data/itemized-data-factory';
import { ItemizedWidgetData } from '../itemized-widget-data/itemized-widget-data';
import { WidgetTableData } from '../widget-table-data';
import { ViewDataTableService } from './view-data-table.service';

@Component({
  selector: 'hb-view-data-widget',
  templateUrl: './view-data-widget.component.html',
})
export class ViewDataWidgetComponent implements OnInit, OnDestroy {
  private unsubscribe: Subject<boolean> = new Subject<boolean>();
  private widget: Widget;
  private itemizedWidgetDataClass: ItemizedWidgetData;

  public tableConfig: TableConfig;
  public tableData: any[] = [];
  public simpleView: boolean;

  @Input() set dashboardWidget(dashboardWidget: DashboardWidget) {
    this.itemizedWidgetDataClass = this.itemizedDataFactory.createItemizedWidgetDataClass(dashboardWidget);
    this.widget = dashboardWidget.widget;
    this.setOverallTableConfig(dashboardWidget);
    this.viewDataTableService.setItemizedDataFilterName.next(null);
  }

  @Input() set widgetTableData(tableData: WidgetTableData) {
    this.setOverallTableData(tableData);
  }

  constructor(
    private viewDataTableService: ViewDataTableService,
    private itemizedDataService: ItemizedWidgetDataService,
    private itemizedDataFactory: ItemizedWidgetDataFactory,
  ) {}

  ngOnInit(): void {
    this.viewDataTableService.showItemizedData.pipe(takeUntil(this.unsubscribe)).subscribe(widget => {
      if (widget && widget.widgetId === this.widget.widgetId) {
        this.itemizedWidgetDataClass.setFilterParameter(this.widget, null, true);
        this.initializeItemizedData();
        this.viewDataTableService.setItemizedDataFilterName.next(null);
      }
    });

    this.viewDataTableService.showOverallTable.pipe(takeUntil(this.unsubscribe)).subscribe(dashboardWidget => {
      if (dashboardWidget && dashboardWidget.widget.widgetId === this.widget.widgetId) {
        this.tableConfig = null;
        this.setOverallTableConfig(dashboardWidget);
        this.viewDataTableService.setItemizedDataFilterName.next(null);
      }
    });

    this.viewDataTableService.setOverallTableData.pipe(takeUntil(this.unsubscribe)).subscribe(tableData => {
      if (tableData && tableData.WidgetId === this.widget.widgetId) {
        this.setOverallTableData(tableData);
      }
    });

    this.viewDataTableService.setItemizedDataWidgetParam.pipe(takeUntil(this.unsubscribe)).subscribe(param => {
      if (param && param.widgetId === this.widget.widgetId) {
        this.viewDataTableService.setItemizedDataFilterName.next(param);
        this.itemizedWidgetDataClass.setFilterParameter(this.widget, param, false);
        this.initializeItemizedData();
      }
    });
  }

  private initializeItemizedData() {
    this.tableConfig = this.itemizedWidgetDataClass.createTableConfig();
    this.getItemizedDataTotalRecordCount();
    this.loadItemizedData(0);
    this.tableConfig.first = 0;
  }

  private getItemizedDataTotalRecordCount() {
    this.itemizedDataService
      .getCount(this.widget)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(count => {
        this.tableConfig.totalRecords = count ? count : 0;
      });
  }

  private loadItemizedData(page: number) {
    this.tableConfig.loading = true;
    this.itemizedDataService
      .getItemizedData(this.widget, page, this.tableConfig.rowsPerPage)
      .pipe(take(1))
      .subscribe(result => {
        if (result) {
          this.tableData = result;
        }
        this.tableConfig.loading = false;
      });
  }

  public lazyLoad(event: LazyLoadEvent): void {
    if (event.first !== this.tableConfig.first) {
      this.loadItemizedData(event.first);
    }
  }

  private setOverallTableData(tableData: WidgetTableData): void {
    if (!tableData) {
      return;
    }

    this.setTableData(tableData);
    this.setTableConfig();
    this.setPagination();
  }

  private setOverallTableConfig(dashboardWidget: DashboardWidget): void {
    this.simpleView = this.viewDataTableService.showPercentageColumn(dashboardWidget.widget.widgetDataType);
    this.setTableConfig();
    this.setTableColumns(dashboardWidget, this.simpleView);
  }

  private setTableColumns(dashboardWidget: DashboardWidget, simpleView: boolean) {
    this.tableConfig.columns = this.viewDataTableService.setTableColumns(this.itemizedWidgetDataClass, dashboardWidget, simpleView);
  }

  private setTableConfig() {
    if (!this.tableConfig) {
      this.tableConfig = this.viewDataTableService.initializeTableConfig();
    }
  }

  private setPagination() {
    this.tableConfig.enablePagination = this.tableData.length > this.tableConfig.rowsPerPage;
  }

  private setTableData(tableData: WidgetTableData): void {
    this.tableData = this.viewDataTableService.setTableData(tableData, this.widget, this.simpleView);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }
}
